import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Badge, Button, Card, CardBody, CardHeader, formatCurrency, formatDate } from "@odingard/design-system";
import { ErrorState, LoadingState } from "@odingard/design-system";
import type { CatalogApplicationDetail, CatalogCustomerState } from "@odingard/sdk-client";
import { useAsync } from "../../lib/useAsync";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { usePageMeta } from "../../lib/usePageMeta";

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

// Primary call-to-action, resolved from server-authoritative customer state when
// the visitor is signed in with an active org; otherwise a public trial prompt.
function AppCta({
  app,
  cs,
  onStartTrial,
  busy,
}: {
  app: CatalogApplicationDetail;
  cs: CatalogCustomerState | null;
  onStartTrial: () => void;
  busy: boolean;
}) {
  const navigate = useNavigate();
  const comingSoon = app.lifecycleStatus === "coming_soon";

  // Not provisioned by Studio: never offer a trial or a subscription, whatever
  // the visitor's session says.
  if (app.delivery !== "platform") {
    const href = app.externalUrl ?? "/contact";
    const label = app.delivery === "service" ? "Talk to us" : "Learn more";
    return href.startsWith("http") ? (
      <a href={href} target="_blank" rel="noreferrer">
        <Button>{label}</Button>
      </a>
    ) : (
      <Link to={href}>
        <Button>{label}</Button>
      </Link>
    );
  }

  if (comingSoon) {
    return (
      <Button disabled variant="ghost">
        Coming soon
      </Button>
    );
  }

  // Signed-out visitor: send them into the self-service signup/trial flow.
  if (!cs) {
    return (
      <Link to="/start">
        <Button>Start free trial</Button>
      </Link>
    );
  }

  if (cs.canLaunch) {
    return <Button onClick={() => navigate(cs.route ?? app.route ?? "/studio")}>Launch</Button>;
  }
  if (cs.state === "contact_sales") {
    return (
      <Link to="/contact">
        <Button variant="secondary">Contact sales</Button>
      </Link>
    );
  }
  return (
    <div className="ods-row">
      {cs.canTrial && (
        <Button disabled={busy} onClick={onStartTrial}>
          {busy ? "Starting…" : "Start free trial"}
        </Button>
      )}
      {cs.canSubscribe && (
        <Link to="/pricing">
          <Button variant={cs.canTrial ? "ghost" : "secondary"}>Upgrade</Button>
        </Link>
      )}
    </div>
  );
}

export function MarketplaceApp() {
  const { slug = "" } = useParams();
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const state = useAsync(
    async () => {
      const app = await api.catalogApplication(slug);
      let cs: CatalogCustomerState | null = null;
      if (orgId) {
        try {
          cs = await api.catalogAppState(orgId, slug);
        } catch {
          cs = null;
        }
      }
      return { app, cs };
    },
    [slug, orgId],
  );

  async function startTrial() {
    if (!orgId) return;
    setBusy(true);
    setNotice(null);
    try {
      const res = await api.startAppTrial(orgId, slug);
      setNotice(res.reused ? "Trial already active for this app." : "Free trial started. Enjoy 14 days.");
      await state.reload();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Could not start the trial.");
    } finally {
      setBusy(false);
    }
  }

  const app = state.data?.app;
  const cs = state.data?.cs ?? null;
  usePageMeta(
    app
      ? {
          title: `${app.name} | Odingard Studio`,
          description: app.tagline || app.description,
          path: `/marketplace/apps/${app.slug}`,
          ogType: "product",
        }
      : null,
  );
  const trialDays = cs?.state === "trialing" ? daysUntil(cs.trialEndsAt) : null;

  return (
    <section className="section">
      <div className="container">
        <div style={{ marginBottom: "var(--ods-space-5)" }}>
          <Link to="/marketplace" style={{ color: "var(--ods-color-muted)", fontSize: "var(--ods-text-sm)" }}>
            ← Back to Marketplace
          </Link>
        </div>

        {state.loading && <LoadingState label="Loading application…" />}
        {state.error && <ErrorState title="Application not found" description={state.error.message} onRetry={state.reload} />}

        {app && (
          <>
            {notice && <div className="banner banner--info">{notice}</div>}
            <div className="ods-row" style={{ gap: "var(--ods-space-4)", alignItems: "flex-start" }}>
              <div className="feature__icon" aria-hidden="true" style={{ fontSize: "2rem" }}>
                {app.icon}
              </div>
              <div style={{ flex: 1 }}>
                <div className="ods-row" style={{ gap: "var(--ods-space-3)", alignItems: "center" }}>
                  <h1 style={{ margin: 0 }}>{app.name}</h1>
                  {app.delivery === "service" ? (
                    <Badge tone="info">Service</Badge>
                  ) : app.delivery === "external" ? (
                    <Badge tone="info">Separate product</Badge>
                  ) : app.lifecycleStatus === "coming_soon" ? (
                    <Badge tone="neutral">Coming soon</Badge>
                  ) : app.lifecycleStatus === "deprecated" ? (
                    <Badge tone="warning">Deprecated</Badge>
                  ) : app.lifecycleStatus === "maintenance" ? (
                    <Badge tone="warning">Maintenance</Badge>
                  ) : (
                    <Badge tone="success">Available</Badge>
                  )}
                  {trialDays != null && <Badge tone="info">Trial · {trialDays}d left</Badge>}
                  {app.category && <Badge tone="neutral">{app.category.name}</Badge>}
                </div>
                <p style={{ color: "var(--ods-color-muted)", marginTop: "var(--ods-space-2)" }}>{app.tagline}</p>
                <div style={{ marginTop: "var(--ods-space-4)" }}>
                  <AppCta app={app} cs={cs} onStartTrial={startTrial} busy={busy} />
                </div>
              </div>
            </div>

            {app.maintenanceMessage && <div className="banner banner--warning" style={{ marginTop: "var(--ods-space-4)" }}>{app.maintenanceMessage}</div>}
            {app.deprecationNotice && <div className="banner banner--warning" style={{ marginTop: "var(--ods-space-4)" }}>{app.deprecationNotice}</div>}

            <div className="marketplace-detail" style={{ marginTop: "var(--ods-space-7)", display: "grid", gap: "var(--ods-space-5)" }}>
              <Card>
                <CardHeader title="Overview" />
                <CardBody>
                  <p style={{ margin: 0 }}>{app.description}</p>
                </CardBody>
              </Card>

              {app.entitlements.length > 0 && (
                <Card>
                  <CardHeader title="Capabilities" />
                  <CardBody>
                    <ul style={{ margin: 0, paddingLeft: "1.1rem", display: "grid", gap: "var(--ods-space-2)" }}>
                      {app.entitlements.map((e) => (
                        <li key={e.key}>
                          {e.label}
                          {e.required && <span style={{ color: "var(--ods-color-muted)" }}> · required</span>}
                        </li>
                      ))}
                    </ul>
                  </CardBody>
                </Card>
              )}

              {app.delivery !== "platform" && (
                <Card>
                  <CardHeader title={app.delivery === "service" ? "How it works" : "About this product"} />
                  <CardBody>
                    <p style={{ margin: 0 }}>
                      {app.delivery === "service"
                        ? "Delivered by Odingard practitioners and scoped with you. It is not provisioned from your Studio subscription, so talk to us and we'll size the engagement."
                        : "A separate Odingard product with its own workspace and subscription. It is not included in a Studio plan and is not provisioned from Studio."}
                    </p>
                  </CardBody>
                </Card>
              )}

              {app.delivery === "platform" && (app.prices.month || app.prices.year) && (
                <Card>
                  <CardHeader title="Pricing" />
                  <CardBody>
                    <div className="ods-row" style={{ gap: "var(--ods-space-6)" }}>
                      {app.prices.month && (
                        <div>
                          <div style={{ fontSize: "var(--ods-text-lg)", fontWeight: 600 }}>
                            {formatCurrency(app.prices.month.unitAmount, app.prices.month.currency)}
                          </div>
                          <div style={{ color: "var(--ods-color-muted)", fontSize: "var(--ods-text-sm)" }}>per month</div>
                        </div>
                      )}
                      {app.prices.year && (
                        <div>
                          <div style={{ fontSize: "var(--ods-text-lg)", fontWeight: 600 }}>
                            {formatCurrency(app.prices.year.unitAmount, app.prices.year.currency)}
                          </div>
                          <div style={{ color: "var(--ods-color-muted)", fontSize: "var(--ods-text-sm)" }}>per year</div>
                        </div>
                      )}
                    </div>
                    <p style={{ color: "var(--ods-color-muted)", fontSize: "var(--ods-text-sm)", marginBottom: 0 }}>
                      Priced per firm, not per seat. See the <Link to="/pricing">pricing page</Link> for bundles.
                    </p>
                  </CardBody>
                </Card>
              )}

              {app.trust && (
                <Card>
                  <CardHeader
                    title="Trust & security"
                    action={
                      app.trust.trustCenterUrl ? (
                        <a href={app.trust.trustCenterUrl} target="_blank" rel="noreferrer">
                          <Button variant="ghost" size="sm">
                            Trust Center
                          </Button>
                        </a>
                      ) : undefined
                    }
                  />
                  <CardBody>
                    <p style={{ marginTop: 0 }}>{app.trust.dataHandling}</p>
                    {app.trust.certifications.length > 0 && (
                      <div className="ods-row" style={{ gap: "var(--ods-space-2)", flexWrap: "wrap", marginBottom: "var(--ods-space-3)" }}>
                        {app.trust.certifications.map((c) => (
                          <Badge key={c} tone="neutral">
                            {c}
                          </Badge>
                        ))}
                      </div>
                    )}
                    {app.trust.securityNotes && (
                      <p style={{ color: "var(--ods-color-muted)", fontSize: "var(--ods-text-sm)", marginBottom: 0 }}>{app.trust.securityNotes}</p>
                    )}
                  </CardBody>
                </Card>
              )}

              {app.release && (
                <Card>
                  <CardHeader title="Release" />
                  <CardBody>
                    <div className="ods-row" style={{ gap: "var(--ods-space-3)", alignItems: "center" }}>
                      <Badge tone="info">v{app.release.currentVersion}</Badge>
                      {app.release.releasedAt && (
                        <span style={{ color: "var(--ods-color-muted)", fontSize: "var(--ods-text-sm)" }}>
                          Released {formatDate(app.release.releasedAt)}
                        </span>
                      )}
                      {app.release.releaseNotesUrl && (
                        <a href={app.release.releaseNotesUrl} target="_blank" rel="noreferrer" style={{ fontSize: "var(--ods-text-sm)" }}>
                          Release notes
                        </a>
                      )}
                    </div>
                    {app.release.knownLimitations && (
                      <p style={{ color: "var(--ods-color-muted)", fontSize: "var(--ods-text-sm)", marginBottom: 0 }}>
                        Known limitations: {app.release.knownLimitations}
                      </p>
                    )}
                    {app.versions.length > 0 && (
                      <ul style={{ marginTop: "var(--ods-space-3)", marginBottom: 0, paddingLeft: "1.1rem", display: "grid", gap: "var(--ods-space-2)" }}>
                        {app.versions.map((v) => (
                          <li key={v.version}>
                            <strong>v{v.version}</strong>
                            {v.isCurrent && <Badge tone="success">Current</Badge>}{" "}
                            <span style={{ color: "var(--ods-color-muted)", fontSize: "var(--ods-text-sm)" }}>
                              {formatDate(v.releasedAt)}: {v.notes}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </CardBody>
                </Card>
              )}

              {app.bundles.length > 0 && (
                <Card>
                  <CardHeader title="Available in bundles" />
                  <CardBody>
                    <div className="ods-row" style={{ gap: "var(--ods-space-2)", flexWrap: "wrap" }}>
                      {app.bundles.map((b) => (
                        <Link key={b.slug} to={`/marketplace/bundles/${b.slug}`}>
                          <Button variant="secondary" size="sm">
                            {b.name}
                          </Button>
                        </Link>
                      ))}
                    </div>
                  </CardBody>
                </Card>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
